import React, { useMemo, useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { cxAlwaysInTouchConstants } from "../../../../../_redux/_constants/cxAlwaysInTouch.constants";
import { MONTH_PROBLEM_MESSAGE, normaliseMonth } from "../../cxAwardMonth";

const EMPTY_WINNER = {
     dealer: null,
     jobRole: null,
     email: "",
     firstName: "",
     lastName: "",
     month: "",
     awardSubject: "",
     rewardValueGbp: cxAlwaysInTouchConstants.CX_DEFAULT_REWARD_GBP,
};

const CxAddWinnerManualForm = ({
     dealerOptions,
     jobRoleOptions,
     isSubmitting,
     onDealerSearch,
     onSubmit,
}) => {
     const [winner, setWinner] = useState(EMPTY_WINNER);
     const [showProblems, setShowProblems] = useState(false);

     const set = (field, value) =>
          setWinner((previous) => ({ ...previous, [field]: value }));

     // Computed from what is on the form rather than held in state, so it can never go stale.
     const problems = useMemo(() => {
          const found = {};

          if (!winner.dealer) {
               found.dealer = "Pick the dealer this winner belongs to.";
          }

          if (!winner.jobRole) {
               found.jobRole =
                    "Pick the role to give a winner who has no Equipe account yet.";
          }

          if (!winner.email.trim()) {
               found.email = "Email is required.";
          }

          if (!winner.firstName.trim()) {
               found.firstName = "First name is required.";
          }

          if (!winner.lastName.trim()) {
               found.lastName = "Last name is required.";
          }

          if (!normaliseMonth(winner.month)) {
               found.month = MONTH_PROBLEM_MESSAGE;
          }

          if (
               winner.rewardValueGbp === null ||
               winner.rewardValueGbp === undefined ||
               winner.rewardValueGbp < 0
          ) {
               found.rewardValueGbp = "Award value cannot be negative.";
          }

          return found;
     }, [winner]);

     const submit = () => {
          if (Object.keys(problems).length) {
               setShowProblems(true);
               return;
          }

          onSubmit([
               {
                    dealershipId: winner.dealer.id,
                    jobRoleId: winner.jobRole.id,
                    email: winner.email.trim(),
                    firstName: winner.firstName.trim(),
                    lastName: winner.lastName.trim(),
                    rewardValueGbp: winner.rewardValueGbp,
                    month: normaliseMonth(winner.month),
                    awardSubject: winner.awardSubject.trim(),
               },
          ]);
     };

     const problem = (field) => (showProblems ? problems[field] : undefined);

     return (
          <Box sx={{ maxWidth: 720 }}>
               <Typography variant="body2" sx={{ mb: 2 }}>
                    A winner who already has an Equipe account keeps the role and
                    dealer they have. The role below is only used for someone who
                    has no account yet.
               </Typography>

               <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ width: 340 }}>
                         <DropDownList
                              style={{ width: "100%" }}
                              label="Dealer"
                              data={dealerOptions}
                              textField="label"
                              dataItemKey="id"
                              value={winner.dealer}
                              filterable={true}
                              onFilterChange={(event) =>
                                   onDealerSearch(event.filter?.value || "")
                              }
                              onChange={(event) => set("dealer", event.value)}
                         />
                         {problem("dealer") && (
                              <Typography variant="caption" color="error">
                                   {problem("dealer")}
                              </Typography>
                         )}
                    </Box>

                    <Box sx={{ width: 260 }}>
                         <DropDownList
                              style={{ width: "100%" }}
                              label="Role for a new account"
                              data={jobRoleOptions}
                              textField="name"
                              dataItemKey="id"
                              value={winner.jobRole}
                              onChange={(event) => set("jobRole", event.value)}
                         />
                         {problem("jobRole") && (
                              <Typography variant="caption" color="error">
                                   {problem("jobRole")}
                              </Typography>
                         )}
                    </Box>

                    <Box sx={{ width: 340 }}>
                         <Input
                              style={{ width: "100%" }}
                              label="Email"
                              value={winner.email}
                              onChange={(event) => set("email", event.value)}
                         />
                         {problem("email") && (
                              <Typography variant="caption" color="error">
                                   {problem("email")}
                              </Typography>
                         )}
                    </Box>

                    <Box sx={{ width: 260 }}>
                         <Input
                              style={{ width: "100%" }}
                              label="First name"
                              value={winner.firstName}
                              onChange={(event) => set("firstName", event.value)}
                         />
                         {problem("firstName") && (
                              <Typography variant="caption" color="error">
                                   {problem("firstName")}
                              </Typography>
                         )}
                    </Box>

                    <Box sx={{ width: 260 }}>
                         <Input
                              style={{ width: "100%" }}
                              label="Last name"
                              value={winner.lastName}
                              onChange={(event) => set("lastName", event.value)}
                         />
                         {problem("lastName") && (
                              <Typography variant="caption" color="error">
                                   {problem("lastName")}
                              </Typography>
                         )}
                    </Box>

                    <Box sx={{ width: 260 }}>
                         <Input
                              style={{ width: "100%" }}
                              label="Month"
                              value={winner.month}
                              placeholder="August 2026"
                              onChange={(event) => set("month", event.value)}
                         />
                         {problem("month") && (
                              <Typography variant="caption" color="error">
                                   {problem("month")}
                              </Typography>
                         )}
                    </Box>

                    {/* Optional. A file upload takes this from the heading above the winner's
                        block, and a winner added by hand has no heading to read. */}
                    <Box sx={{ width: 260 }}>
                         <Input
                              style={{ width: "100%" }}
                              label="Award for"
                              value={winner.awardSubject}
                              placeholder="VoC service survey"
                              onChange={(event) =>
                                   set("awardSubject", event.value)
                              }
                         />
                    </Box>

                    <Box sx={{ width: 260 }}>
                         <NumericTextBox
                              style={{ width: "100%" }}
                              label="Award value (GBP)"
                              format="n2"
                              min={0}
                              value={winner.rewardValueGbp}
                              onChange={(event) =>
                                   set("rewardValueGbp", event.value)
                              }
                         />
                         {problem("rewardValueGbp") && (
                              <Typography variant="caption" color="error">
                                   {problem("rewardValueGbp")}
                              </Typography>
                         )}
                    </Box>

               </Box>

               <Box sx={{ pt: 3 }}>
                    <Button
                         type="button"
                         themeColor="primary"
                         disabled={isSubmitting}
                         onClick={submit}
                    >
                         {isSubmitting ? "Adding..." : "Add winner"}
                    </Button>
               </Box>
          </Box>
     );
};

export default CxAddWinnerManualForm;
